import React, { useEffect, useState } from 'react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { Helmet } from 'react-helmet';
import api from '../../services/api';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../../assets/css/video.css';

const Video = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featuredVideo, setFeaturedVideo] = useState(null);

  // Function to extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Function to get YouTube thumbnail URL
  const getYouTubeThumbnail = (url, quality = 'hqdefault') => {
    const videoId = getYouTubeId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await api.get('/videos');
        
        if (!response.data || !response.data.success || !Array.isArray(response.data.data)) {
          throw new Error('Invalid data format received from API');
        }
        
        // Enhance videos with YouTube thumbnails
        const enhancedVideos = response.data.data.map(video => ({
          ...video,
          youtube_thumbnail: getYouTubeThumbnail(video.video_url)
        }));
        
        setVideos(enhancedVideos);
        
        const activeVideos = enhancedVideos.filter(video => Boolean(video.is_active));
        
        if (activeVideos.length > 0) {
          setFeaturedVideo(activeVideos[0]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError(err.message || 'Failed to load videos');
        setLoading(false);
      }
    };
    
    fetchVideos();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  if (loading) {
    return (
      <div className="video-page">
        <Helmet><title>Loading Videos...</title></Helmet>
        <Navbar />
        <div className="container text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading video content...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-page">
        <Helmet><title>Error Loading Videos</title></Helmet>
        <Navbar />
        <div className="container text-center py-5">
          <div className="alert alert-danger">
            <h5>Error Loading Videos</h5>
            <p>{error}</p>
            <button 
              className="btn btn-primary mt-2"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="video-page">
      <Helmet>
        <title>Videos - Startup Business Partners</title>
      </Helmet>
      
      <Navbar />
      
      <section className="breadcrumb_area breadcrumb1">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="breadcrumb_wrapper d-flex align-items-center justify-content-between flex-wrap">
                <h4 className="page_title">Videos</h4>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <a href="/" style={{cursor: "pointer"}}>Home</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">Videos</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="videos-wrapper video--one">
        <div className="container">
          {featuredVideo ? (
            <div className="row align-items-md-center mb-5">
              <div className="col-lg-5 col-md-6 margin-sm-60">
                <h3 className="color-dark">Featured Video: <span>{featuredVideo.title}</span></h3>
                <p>{featuredVideo.description}</p>
                <a 
                  href={featuredVideo.video_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-primary btn--rounded mt-4"
                >
                  Watch Full Video
                </a>
              </div>
              <div className="col-lg-6 col-md-6 offset-lg-1">
                <div className="video-single">
                  <figure>
                    <div className="v_img">
                      {featuredVideo.youtube_thumbnail ? (
                        <img 
                          src={featuredVideo.youtube_thumbnail} 
                          alt={featuredVideo.title} 
                          className="rounded" 
                          onError={(e) => {
                            e.target.onerror = null; 
                            e.target.src = featuredVideo.thumbnail_url || '/images/video-placeholder.jpg';
                          }}
                        />
                      ) : featuredVideo.thumbnail_url ? (
                        <img 
                          src={featuredVideo.thumbnail_url} 
                          alt={featuredVideo.title} 
                          className="rounded" 
                        />
                      ) : (
                        <div className="video-placeholder">
                          <span>No Thumbnail Available</span>
                        </div>
                      )}
                    </div>
                    <figcaption className="d-flex justify-content-center align-items-center shape-wrapper img-shape-two">
                      <a 
                        href={featuredVideo.video_url} 
                        className="video-iframe play-btn play-btn--lg play-btn--primary"
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 415.346 415.346" 
                          className="svg replaced-svg"
                        >
                          <g>
                            <path d="M41.712,415.346c-11.763,0-21.3-9.537-21.3-21.3V21.299C20.412,9.536,29.949,0,41.712,0l346.122,191.697c0,0,15.975,15.975,0,31.951C371.859,239.622,41.712,415.346,41.712,415.346z"></path>
                          </g>
                        </svg>
                      </a>
                    </figcaption>
                  </figure>
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-warning text-center">
              No featured video available
            </div>
          )}

          <div className="row">
            <div className="col-md-12">
              <h3 className="text-center mb-5">More Videos</h3>
              {videos.length > 0 ? (
                <Slider {...sliderSettings}>
                  {videos.filter(v => v.is_active).map(video => (
                    <div key={video.id} className="px-2">
                      <div className="video-card">
                        <div className="video-thumbnail">
                          {video.youtube_thumbnail ? (
                            <img 
                              src={video.youtube_thumbnail} 
                              alt={video.title} 
                              className="img-fluid rounded"
                              onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = video.thumbnail_url || '/images/video-placeholder.jpg';
                              }}
                            />
                          ) : video.thumbnail_url ? (
                            <img 
                              src={video.thumbnail_url} 
                              alt={video.title} 
                              className="img-fluid rounded"
                            />
                          ) : (
                            <div className="video-placeholder">
                              <span>No Thumbnail</span>
                            </div>
                          )}
                          <a 
                            href={video.video_url} 
                            className="play-icon"
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <i className="las la-play"></i>
                          </a>
                        </div>
                        <div className="video-info p-3">
                          <h5>{video.title}</h5>
                          <p className="text-muted">{video.description}</p>
                          <a 
                            href={video.video_url} 
                            className="btn btn-sm btn-outline-primary"
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            Watch Now
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </Slider>
              ) : (
                <div className="alert alert-info text-center">No videos available</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Video;